import axios from 'axios';

// We'll import notification context dynamically to avoid circular dependencies
let showNotificationError = null;

// Function to set notification handler (called from App)
export const setNotificationHandler = (handler) => {
  showNotificationError = handler;
};

// Determine API URL based on environment variables
// To switch between local/offline and online backend, comment/uncomment the REACT_APP_API_URL line in .env file
const getApiUrl = () => {
  // Use REACT_APP_API_URL if set (comment/uncomment different values in .env to switch)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  // Default fallback to localhost if no env var is set
  console.warn('⚠️ REACT_APP_API_URL not set in .env file. Using default: http://localhost:5000/api');
  return 'https://aigeniusbackend.vercel.app/api';
};

const API_URL = getApiUrl();

// Log API URL in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('🔗 API URL:', API_URL);
}

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Always send cookies
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Cookies are sent automatically with withCredentials: true
    // But we can also support Authorization header as fallback
    const token = localStorage.getItem('token');
    if (token && !config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle token refresh and errors
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;

    // Don't try to refresh token for login/register/auth endpoints (they return 401 for invalid credentials)
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') ||
      originalRequest?.url?.includes('/auth/register') ||
      originalRequest?.url?.includes('/auth/refresh') ||
      originalRequest?.url?.includes('/auth/me');

    // If error is 401 and we haven't tried to refresh yet (and it's not an auth endpoint)
    if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Try to refresh token
        const refreshResponse = await api.post('/auth/refresh', {}, { withCredentials: true });
        if (refreshResponse && refreshResponse.data && refreshResponse.data.token) {
          localStorage.setItem('token', refreshResponse.data.token);
          // Also update the original request with the new token immediately
          originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.token}`;
        }
        processQueue(null, refreshResponse?.data?.token || null);
        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        // Refresh failed - show notification and redirect to login
        if (showNotificationError) {
          showNotificationError('Your session has expired. Please login again.');
        }
        if (window.location.pathname !== '/login') {
          localStorage.removeItem('token');
          setTimeout(() => {
            window.location.href = '/login';
          }, 2000);
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 Forbidden (unauthorized)
    if (error.response?.status === 403) {
      const message = error.response?.data?.error || 'You do not have permission to perform this action.';
      if (showNotificationError) {
        showNotificationError(message);
      }
      return Promise.reject(new Error(message));
    }

    // Handle 401 Unauthorized (not logged in) - but not for auth endpoints
    if (error.response?.status === 401 && !isAuthEndpoint) {
      const message = error.response?.data?.error || 'Authentication required. Please login.';
      if (showNotificationError) {
        showNotificationError(message);
      }
      // Don't redirect here if we're already on login page
      if (window.location.pathname !== '/login' && !originalRequest._retry) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
      return Promise.reject(new Error(message));
    }

    // Handle 401 for auth endpoints (login/register failures) - don't show notification here,
    // let the component handle it, but ensure error message is properly extracted
    if (error.response?.status === 401 && isAuthEndpoint) {
      const message = error.response?.data?.error || error.message || 'Authentication failed';
      return Promise.reject(new Error(message));
    }

    // Handle other errors
    const message =
      error.response?.data?.error || error.message || 'Something went wrong';

    // Show notification for server errors (500, etc.)
    if (error.response?.status >= 500 && showNotificationError) {
      showNotificationError('Server error. Please try again later.');
    }

    return Promise.reject(new Error(message));
  }
);

export default api;
